import {
  CashBookController_cashBookPage_v1,
  type CashBookEntryResource,
  type CashBookTotalsResource,
  ExportController_cashBookCsv_v1,
  type OutletResource,
  OutletsController_list_v1,
} from '@repo/api-client';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconDownload, IconLoader2 } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isForbidden, messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { formatInstant } from '@/lib/instant';
import { formatPaise } from '@/lib/money';

import { currentCursor, type CursorTrail, FIRST_PAGE } from '../back-office/cursor-pagination';
import { CursorPager } from '../back-office/CursorPager';
import { DayPicker } from '../back-office/DayPicker';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';
import {
  describeDirection,
  labelForEntryPaymentMode,
  labelForReferenceType,
  NARROWABLE_REFERENCE_TYPES,
} from './cash-book-entries';
import { CASH_BOOK_DEFAULT_FILENAME, saveDownloadedFile } from './cash-book-export';
import { defaultPeriod, periodProblem, type ReportingPeriod } from './reporting-period';

/**
 * Every entry that moved money, in the order it happened:
 * `GET /v1/reports/cash-book`, with `GET /v1/reports/cash-book.csv` beside it.
 *
 * **The read that answers "where did the money go" without re-deriving it from
 * four screens.** Sales, refunds, ticket issues, voids and till payouts all
 * write a ledger entry and all appear here, in one list, oldest first — because
 * a cash book is read forward.
 *
 * ## What is deliberately not here
 *
 * **An expense.** It writes no ledger entry, so it is not a cash-book row; it
 * appears in `GET /v1/expenses`, which is the screen beside this one, and in the
 * profit and loss's expense figure. A reader who adds this screen's takings to
 * that screen's spending has not double-counted anything, and a screen that
 * quietly merged the two would make it look as though they had.
 *
 * **Cost and margin.** Neither is on this route, which is exactly why it is not
 * owner-only: `export.controller.ts` splits the epic on that line, and the
 * profit and loss carries both and is `@RequireRole('OWNER')` because of it.
 *
 * ## The sign lives in `direction`, never in the amount
 *
 * `amountPaise` is "a non-negative magnitude on every row, reversals included",
 * and the direction is its own field so that "a magnitude cannot carry a second
 * sign hiding behind the first". The table therefore has an In/Out column and
 * renders every amount unsigned. A screen that showed a debit as `−₹450.00`
 * would be publishing a number the API never did, and anybody adding the column
 * would get a total that disagrees with the envelope's.
 *
 * ## Totals are the period's, not the page's
 *
 * `totals` is "the whole period's, identical on every page, and identical to the
 * totals row of the CSV over the same period" (FR-717). So they sit in the rail
 * and do **not** move as you page — which is the opposite of what a footer under
 * the table would imply. Nothing here sums a column; `docs/ui-conventions.md`
 * says the client does not compute totals, and here there is a published one to
 * take instead.
 *
 * ## A reversal and the entry it reverses both appear
 *
 * Neither is hidden, netted away, or presented as a modification of the other
 * (FR-715). `reversesEntryId` links them, the row says so, and nothing resolves
 * a chain — "there is no effective amount and no resolved chain", because that
 * would be a stored derived value.
 *
 * ## Paging, and the known slowness behind it
 *
 * Keyset, `{ items, limit, nextCursor }` with no total, so this takes
 * `cursor-pagination.ts` and `CursorPager` rather than `Pager` — the same pair
 * FE049 established, and the module says why a single pager would be a type with
 * a hole in it. Nothing retries paging on its own: `blossom-rest-api#347` has an
 * out-of-range cursor answering a *retryable* 500, which is what a retry loop
 * would spin on.
 *
 * `blossom-rest-api#346` is filed and not yet fixed: the park-wide cash book is
 * not index-driven, so it is correct and will get slower as the ledger grows.
 * Worth knowing before this screen is reported as a UI performance problem.
 */

/** Every state the list can be in. One value, so two cannot coexist. */
type ListState =
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly items: readonly CashBookEntryResource[];
      readonly nextCursor: string | null;
      readonly totals: CashBookTotalsResource;
    }
  | { readonly kind: 'failed'; readonly error: unknown };

/** Where the export has got to. The failure is worded, so it is carried. */
type ExportState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running' }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The "not filtering" values. Not `''`, which Base UI clears to. */
const EVERY_OUTLET = 'every-outlet';
const EVERY_KIND = 'every-kind';

const COLUMNS = 6;

export function CashBook() {
  const client = useApiClient();

  // The period the list is being read over, and the period the pickers show.
  // The same value until somebody starts changing it: a half-cleared range is
  // not a request, so the last complete one stays on screen.
  const [period, setPeriod] = useState<ReportingPeriod>(() => defaultPeriod(systemClock));

  const [outletId, setOutletId] = useState<string>(EVERY_OUTLET);
  const [referenceType, setReferenceType] = useState<string>(EVERY_KIND);

  const [trail, setTrail] = useState<CursorTrail>(FIRST_PAGE);

  const [state, setState] = useState<ListState>({ kind: 'loading' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);

  const [exporting, setExporting] = useState<ExportState>({ kind: 'idle' });

  const problem = periodProblem(period, systemClock);

  const cursor = currentCursor(trail);

  const complete = period.from !== '' && period.to !== '' && problem === null;

  /**
   * The filters as the routes take them, built once and used by both.
   *
   * The list and the export must ask the same question — the CSV is the same
   * statement as the read, "one statement builder with two serialisers, so the
   * two cannot drift" — and two call sites assembling this separately is how a
   * filter eventually reaches one and not the other.
   */
  const filters = useMemo(
    () => ({
      from: period.from,
      to: period.to,
      // A filter narrows within reach and never widens it, so "every outlet" is
      // the field omitted rather than a value meaning all.
      outletId: outletId === EVERY_OUTLET ? undefined : outletId,
      referenceType:
        referenceType === EVERY_KIND
          ? undefined
          : (referenceType as (typeof NARROWABLE_REFERENCE_TYPES)[number]),
    }),
    [period.from, period.to, outletId, referenceType],
  );

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. Retired ones included —
    // an outlet retired last month still took money the month before.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setOutlets(all);
      })
      .catch(() => {
        // Only the outlet filter is affected. Every row carries `outletName`, so
        // the table itself never depended on this read.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    // A half-filled or refused period is not a request. The notice under the
    // pickers is already saying so, and sending it would earn a 422 for
    // something the screen can see.
    if (!complete) return;

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(CashBookController_cashBookPage_v1, {
        query: {
          ...filters,
          // Sent back verbatim, `undefined` on the first page. Opaque: never
          // constructed here.
          cursor: cursor ?? undefined,
        },
        signal: controller.signal,
      })
      .then((answer) => {
        if (live) {
          setState({
            kind: 'loaded',
            items: answer.items,
            nextCursor: answer.nextCursor,
            totals: answer.totals,
          });
        }
      })
      .catch((error: unknown) => {
        // An abort is this screen's own doing — a filter changed, or it
        // unmounted — and is not a failure to report.
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, filters, cursor, complete, reloads]);

  /**
   * A filter changed, so the trail is worthless.
   *
   * A cursor names a position in one ordered result set; carried into a
   * different one it is at best meaningless and at worst the out-of-range
   * instant `blossom-rest-api#347` answers a retryable 500 for.
   */
  const changeFilter = (apply: () => void) => {
    apply();
    setTrail(FIRST_PAGE);
  };

  /**
   * The CSV, over the period and filters the table is already showing.
   *
   * **Through `client.download` and not an anchor**, and that is not a
   * preference: the route reads its principal from `Authorization: Bearer` and
   * from nowhere else, so a plain navigation would arrive unauthenticated and
   * save a `401` envelope named `.csv`. `cash-book-export.ts` sets the whole of
   * it out, including why the filename is rebuilt here.
   *
   * **No cursor.** The export is the whole period, not the page on screen —
   * that is what makes its totals row equal the envelope's totals (FR-717).
   */
  const runExport = () => {
    if (!complete || exporting.kind === 'running') return;

    setExporting({ kind: 'running' });

    client
      .download(ExportController_cashBookCsv_v1, { query: filters })
      .then((file) => {
        saveDownloadedFile(file, CASH_BOOK_DEFAULT_FILENAME);
        setExporting({ kind: 'idle' });
      })
      .catch((error: unknown) => {
        setExporting({ kind: 'failed', error });
      });
  };

  // Base UI resolves what the trigger shows from `items` and not from the
  // rendered options, so a picker without it shows a bare UUID where a name
  // belongs. Derived from the same rows the options render.
  const outletItems = useMemo(
    () => [
      { label: 'Every outlet', value: EVERY_OUTLET },
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets],
  );

  const kindItems = useMemo(
    () => [
      { label: 'Every kind', value: EVERY_KIND },
      ...NARROWABLE_REFERENCE_TYPES.map((kind) => ({
        label: labelForReferenceType(kind),
        value: kind,
      })),
    ],
    [],
  );

  const items = state.kind === 'loaded' ? state.items : [];

  const forbidden = state.kind === 'failed' && isForbidden(state.error);

  return (
    <Screen
      title="Cash book"
      action={
        <Button
          onClick={runExport}
          // Nothing to export while the period is refused, and the API would
          // answer the same 422 the notice under the pickers is already giving.
          disabled={!complete || exporting.kind === 'running'}
        >
          {exporting.kind === 'running' ? (
            <IconLoader2 data-icon="inline-start" className="animate-spin" />
          ) : (
            <IconDownload data-icon="inline-start" />
          )}
          {exporting.kind === 'running' ? 'Exporting…' : 'Export CSV'}
        </Button>
      }
      aside={
        <>
          <div className="space-y-2">
            <Label htmlFor="cash-book-from" className="text-sm text-muted-foreground">
              From
            </Label>
            <DayPicker
              id="cash-book-from"
              value={period.from}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              onValueChange={(next) => {
                changeFilter(() => {
                  setPeriod((held) => ({ ...held, from: next }));
                });
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cash-book-to" className="text-sm text-muted-foreground">
              To
            </Label>
            <DayPicker
              id="cash-book-to"
              value={period.to}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              onValueChange={(next) => {
                changeFilter(() => {
                  setPeriod((held) => ({ ...held, to: next }));
                });
              }}
            />
          </div>

          {/* Under the pickers rather than above the table: it is about the two
              controls immediately above it, and it is not a failed load. */}
          {problem !== null && (
            <p role="status" className="text-sm text-destructive">
              {problem}
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="cash-book-outlet" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              onValueChange={(next) => {
                changeFilter(() => {
                  // `null` is Base UI's "cleared", which this picker has no
                  // affordance for. Read as "not filtering" rather than cast
                  // away, so it can never become `outletId=null` in the query.
                  setOutletId(next ?? EVERY_OUTLET);
                });
              }}
            >
              <SelectTrigger
                id="cash-book-outlet"
                aria-label="Outlet"
                disabled={outlets.length === 0}
                className="h-10 w-full bg-card"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={EVERY_OUTLET}>Every outlet</SelectItem>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cash-book-kind" className="text-sm text-muted-foreground">
              Kind
            </Label>
            <Select
              value={referenceType}
              items={kindItems}
              onValueChange={(next) => {
                changeFilter(() => {
                  setReferenceType(next ?? EVERY_KIND);
                });
              }}
            >
              <SelectTrigger id="cash-book-kind" aria-label="Kind" className="h-10 w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={EVERY_KIND}>Every kind</SelectItem>
                  {NARROWABLE_REFERENCE_TYPES.map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      {labelForReferenceType(kind)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <ScreenFact label="Period">
            {period.from === '' || period.to === ''
              ? 'Choose both days.'
              : `${formatBusinessDate(period.from)} to ${formatBusinessDate(period.to)}`}
          </ScreenFact>

          {/* The period's, from the envelope — identical on every page and
              identical to the CSV's totals row. Nothing here sums a column. */}
          {state.kind === 'loaded' && (
            <ScreenFact label="This period">
              <span className="text-muted-foreground">Taken in</span>{' '}
              <span className="font-mono numeric">{formatPaise(state.totals.takingsPaise)}</span>
              <br />
              <span className="text-muted-foreground">Handed back</span>{' '}
              <span className="font-mono numeric">{formatPaise(state.totals.returnedPaise)}</span>
              <br />
              <span className="text-muted-foreground">Net</span>{' '}
              <span className="font-mono numeric">{formatPaise(state.totals.netPaise)}</span>
              <br />
              <span className="font-mono numeric">{state.totals.entryCount}</span>{' '}
              <span className="text-muted-foreground">
                {state.totals.entryCount === 1 ? 'entry in all' : 'entries in all'}
              </span>
            </ScreenFact>
          )}

          {/* A form-level failure goes where the control is, not in a toast:
              toasts vanish and somebody who looked away cannot get it back. */}
          {exporting.kind === 'failed' && (
            <p role="status" className="text-sm text-destructive">
              The export could not be made. {messageForFailure(exporting.error)}
            </p>
          )}
        </>
      }
    >
      {/* `stated`, with a width on **every** column, added by FE068 when the
          sheet took the whole field and the `68rem` cap went. "What happened"
          was the one column naming no width and it took the whole leftover —
          about 800px holding "SALE / Cash", with the person who did it stranded
          on the far side of it. Stating all six lets fixed layout share the
          slack out in proportion instead of pooling it in one place, and Amount
          still ends on the sheet's right edge, which is where the figures are
          read down. The same change and the same reasoning as
          `StockMovements.tsx`; the comment there carries the long version. */}
      <ScreenTable label="Cash book" columns="stated">
        <TableHeader>
          <TableRow>
            {/* Leads with the trading day because that is what the period
                matches on, and it is the server's answer rather than the clock
                of whatever till wrote the row. */}
            <TableHead className="w-40">Trading day</TableHead>
            <TableHead className="w-32">Outlet</TableHead>
            <TableHead className="w-60">What happened</TableHead>
            <TableHead className="w-40">Who and where</TableHead>
            <TableHead className="w-20">In or out</TableHead>
            {/* Amounts right-align, per docs/ui-conventions.md — comparing down
                a column is the task. */}
            <TableHead className="w-32 text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

        {state.kind === 'failed' && (
          <TableNotice columns={COLUMNS}>
            {forbidden ? (
              <EmptyState
                title="This account cannot read the cash book"
                description="If you are an owner or manager, sign out and back in."
              />
            ) : (
              <LoadFailed error={state.error} onRetry={() => setReloads((count) => count + 1)} />
            )}
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (items.length === 0 ? (
            <TableNotice columns={COLUMNS}>
              <EmptyState
                title="No money moved in this period"
                description={
                  referenceType === EVERY_KIND
                    ? 'Widen the period, or try every outlet.'
                    : 'Try every kind, or widen the period.'
                }
              />
            </TableNotice>
          ) : (
            <TableBody>
              {/* In the order the API sent them — oldest first by `recordedAt`
                  with the id breaking a tie, because a cash book is read
                  forward. Nothing here sorts, filters, nets a reversal off, or
                  adds a row. */}
              {items.map((movement) => (
                <EntryRow key={movement.id} entry={movement} />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {/* Not while the load failed: the notice above already says the list could
          not be read, and a pager under it offers to move through rows that are
          not there. */}
      {state.kind === 'loaded' && (
        <CursorPager
          trail={trail}
          rows={items.length}
          nextCursor={state.nextCursor}
          onTrailChange={setTrail}
          label="cash book"
          noun="entry"
        />
      )}
    </Screen>
  );
}

/** One movement. */
function EntryRow({ entry }: { entry: CashBookEntryResource }) {
  const direction = describeDirection(entry.direction);

  return (
    <TableRow>
      <TableCell className="py-3.5 align-top">
        <span className="numeric">{formatBusinessDate(entry.businessDate)}</span>
        <br />
        {/* The authoritative instant, and the half of the keyset cursor that
            orders this list. `occurredAt` is the device's account and is not
            trusted to order anything (FR-008), so it is not what is shown here. */}
        <span className="text-xs text-muted-foreground">
          Recorded {formatInstant(entry.recordedAt)}
        </span>
      </TableCell>

      {/* `outletName` comes on the row, so nothing here resolves an id and no
          cell can fall back to a bare UUID. Keyed on `outletId` everywhere it
          matters, because two live outlets may share a name. */}
      <TableCell className="align-top">{entry.outletName}</TableCell>

      <TableCell className="align-top whitespace-normal">
        {labelForReferenceType(entry.referenceType)}
        <br />
        <span className="text-xs text-muted-foreground">
          {labelForEntryPaymentMode(entry.paymentMode)}
        </span>
        {entry.reversesEntryId !== null && (
          <>
            <br />
            {/* Both rows appear and neither is presented as a modification of
                the other (FR-715). This says which one this is; it does not
                resolve a chain or net an amount off, because there is no
                effective amount to publish. */}
            <span className="text-xs text-muted-foreground">
              Reverses an earlier entry in this book.
            </span>
          </>
        )}
      </TableCell>

      <TableCell className="align-top">
        {entry.actorName}
        <br />
        <span className="text-xs text-muted-foreground">
          {/* Null is "no device originated it", which is the honest answer
              rather than an invented till (FR-129). A machine sale is null too,
              but it was not the back office either — it came off a billing
              machine, which the cause column already says — so it is blank. */}
          {entry.terminalName ?? (entry.referenceType === 'MACHINE_SALE' ? null : 'Back office')}
        </span>
      </TableCell>

      <TableCell className="align-top">
        {/* `outline` and not `destructive`: money handed back is an ordinary
            movement, not a refusal or a warning. The sign lives here rather than
            on the amount beside it. */}
        <Badge variant="outline" title={direction.description}>
          {direction.label}
        </Badge>
      </TableCell>

      <TableCell className="text-right align-top font-mono numeric">
        {/* Unsigned, always: a non-negative magnitude on every row, reversals
            included. The direction column carries the sign. */}
        {formatPaise(entry.amountPaise)}
      </TableCell>
    </TableRow>
  );
}
